'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import useIsIframe from './use-is-iframe';

// Namespaced message contract sent to the embedding parent (e.g. the Overwolf
// app shell), so it can render a navigation progress bar above the iframe.
// Non-Overwolf embedders simply ignore messages they don't recognise.
export const IFRAME_NAV_MESSAGE_SOURCE = 'ow-store';
export type IframeNavMessageType = 'navigate-start' | 'navigate-end';

// -----------------------------------------------------------------------------
// `url` lets the embedding parent log/track where the frame navigated — it can't
// read this cross-origin frame's location itself. navigate-end sends the settled
// location; navigate-start sends the click's resolved destination.
const postNavEvent = (type: IframeNavMessageType, url?: string): void => {
  if (typeof window === 'undefined' || window.parent === window) {
    return;
  }

  window.parent.postMessage(
    { source: IFRAME_NAV_MESSAGE_SOURCE, type, url },
    '*',
  );
};

// -----------------------------------------------------------------------------
// Determines whether a click on an anchor will navigate the iframe itself
// (and therefore should trigger a progress bar) vs. open elsewhere / do nothing.
const willNavigateIframe = (event: MouseEvent): boolean => {
  // Only un-modified primary-button clicks navigate the current frame.
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false;
  }

  const anchor = (event.target as HTMLElement | null)?.closest('a');
  const href = anchor?.getAttribute('href');
  if (!anchor || !href) {
    return false;
  }

  // Links that open in a new tab/window don't navigate this frame.
  if (anchor.target && anchor.target !== '_self') {
    return false;
  }

  // Skip non-navigational / external protocols (deep links, mail, etc.).
  if (/^(mailto:|tel:|overwolf:|javascript:)/i.test(href)) {
    return false;
  }

  // Only same-origin navigations move the iframe; other origins open elsewhere.
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) {
      return false;
    }

    // Same-document navigations (the #fragment or ?query changes but the path
    // doesn't) don't unload or remount the page, so no `navigate-end` follows —
    // `navigate-end` is keyed off a pathname change. Announcing them as
    // navigations would leave the parent waiting on a navigation that never
    // completes: a stuck progress bar, and a false connection-error overlay in
    // the Overwolf shell's navigation watchdog. They aren't page navigations.
    return url.pathname !== window.location.pathname;
  } catch {
    return false;
  }
};

// -----------------------------------------------------------------------------
/**
 * Bridges in-iframe navigation to the embedding parent via postMessage so the
 * parent (the Overwolf app shell) can show a progress bar above the iframe.
 *
 * - `navigate-start` is posted when a click will navigate the frame.
 * - `navigate-end` is posted on every route settle / fresh document mount,
 *   which covers both soft (next/link) and hard (full reload) navigations.
 */
export const useIframeNavigationBridge = (): void => {
  const isIframe = useIsIframe();
  const pathname = usePathname();

  // Navigation finished: runs on mount (a freshly loaded document means a hard
  // navigation just completed) and whenever the route changes (soft nav).
  useEffect(() => {
    if (!isIframe) {
      return;
    }

    postNavEvent('navigate-end', window.location.href);
  }, [isIframe, pathname]);

  // Navigation started: intercept link clicks before the document unloads.
  useEffect(() => {
    if (!isIframe) {
      return;
    }

    const onClick = (event: MouseEvent): void => {
      if (willNavigateIframe(event)) {
        const href = (event.target as HTMLElement | null)
          ?.closest('a')
          ?.getAttribute('href');
        const dest = href
          ? new URL(href, window.location.href).href
          : undefined;
        postNavEvent('navigate-start', dest);
      }
    };

    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('click', onClick, true);
    };
  }, [isIframe]);
};

export default useIframeNavigationBridge;
